import { CommentHubNotificationGroup } from 'shared-code/commenthub-api'

export interface NotificationLine {
  /** Rendered bold, ahead of `rest`. Null for system events, which have no actor. */
  lead: string | null
  rest: string
  /** Deep link into the thread, or null when the event has no post. */
  href: string | null
}

const unknownActor = 'Someone'

/** Promotion copy mirrors commentHub's fallbackText; `user` (a demotion) falls through to "Your role changed to User". */
const roleLabels: Record<string, string> = {
  trusted: 'a Trusted User',
  moderator: 'a Moderator',
  staff: 'Staff',
  admin: 'an Admin',
}

/**
 * A service event's link (`meta.url`): a root-relative path, or an `https:` URL on the app's OWN
 * host (`appUrl`, the brand's configured origin) — nothing else. The value is another
 * service's, the `<a href>` is ours: no `javascript:`, no `//host`, no other site. Without an
 * `appUrl` only root-relative paths link.
 */
export function siteEventHref(
  group: CommentHubNotificationGroup,
  appUrl?: string | null
): string | null {
  const url = group.meta?.url
  return typeof url === 'string' ? ownSiteHref(url, appUrl) : null
}

/**
 * The link rule behind `siteEventHref`, for any URL another service hands us (a "Your comments"
 * row's `post.url` too): root-relative, or `https:` on the app's own host — else null.
 */
export function ownSiteHref(url: string, appUrl?: string | null): string | null {
  const trimmed = url.trim()
  if (!trimmed || /\s/.test(trimmed)) {
    return null
  }
  if (/^\/(?![/\\])/.test(trimmed)) {
    return trimmed
  }
  if (!appUrl || !/^https:\/\//i.test(trimmed)) {
    return null
  }
  try {
    const target = new URL(trimmed)
    const own = new URL(appUrl)
    return target.protocol === 'https:' && target.host === own.host ? target.href : null
  } catch {
    return null
  }
}

/** What the tray knows that a line may need. */
export interface NotificationLineContext {
  /** The app's own origin (`appUrl`) — the only absolute host a service event may link to. */
  appUrl?: string | null
}

function actorName(group: CommentHubNotificationGroup): string {
  return group.actors[0]?.name?.trim() || unknownActor
}

/**
 * The deep link is the whole point of a notification: it lands the reader on
 * their own comment, not on the article. `#conv-comment-{id}` is the anchor
 * CommentHubConversations already mints and already sets as an element id.
 *
 * NOTE: a comment on page 2+ of a paginated thread is not in the DOM when the
 * page loads, so the hash alone cannot always find it — see the "locate" ask in
 * commentHub `docs/notifications-frontend-asks.md` §3.2. Until that lands, the
 * link degrades to the article, which is why the ask is filed as a blocker.
 */
function hrefFor(group: CommentHubNotificationGroup): string | null {
  const url = group.post?.url
  if (!url) {
    return null
  }
  return group.commentId ? commentAnchorHref(url, group.commentId) : url
}

/** A comment's place in its article's thread: `#conv-comment-<id>` on the post URL (any hash it
 *  carried dropped — one anchor only). The notification rows' and "Your comments" rows' rule. */
export function commentAnchorHref(postUrl: string, commentId: number): string {
  return `${postUrl.split('#')[0]}#conv-comment-${commentId}`
}

/**
 * Turns a group into a line of copy.
 *
 * The type set is OPEN — the server ships new types to bundles already cached in
 * readers' browsers — so the default branch must always produce something
 * renderable. It reads `fallbackText`, a complete sentence the server renders
 * for every group; the cases below are only the types whose wording we want to
 * own. Adding a case is an improvement, never a requirement.
 */
export function notificationLine(
  group: CommentHubNotificationGroup,
  context: NotificationLineContext = {}
): NotificationLine {
  const href = hrefFor(group)

  switch (group.type) {
    case 'reply': {
      const others = group.count - 1
      return {
        lead: actorName(group),
        rest:
          others > 0
            ? `and ${others} other${others === 1 ? '' : 's'} replied to your comment`
            : 'replied to your comment',
        href,
      }
    }
    // Someone upvoted the reader's comment (commentHub accumulates them per comment, up to three
    // faces). The figure follows thread_reply's rule, not reply's: it counts from unreadCount
    // while anything is unread and from count once the row is read — the server's fallbackText
    // reads the same field for the same reason (c420ba1), so the two never disagree. Without a
    // named actor the server's sentence is the line ("4 people liked your comment on …").
    case 'comment_liked': {
      if (!group.actors.length) {
        return {
          lead: null,
          rest: group.fallbackText?.trim() || 'Someone liked your comment',
          href,
        }
      }
      const fresh = group.unreadCount ?? 0
      const others = (fresh > 0 ? fresh : group.count) - 1
      return {
        lead: actorName(group),
        rest:
          others > 0
            ? `and ${others} other${others === 1 ? '' : 's'} liked your comment`
            : 'liked your comment',
        href,
      }
    }
    // The post title is rendered on its own line in the row, so the sentence
    // deliberately drops the contract's trailing "on {post.title}" rather than
    // printing the headline twice.
    case 'comment_featured':
      return { lead: null, rest: 'Your comment was pinned', href }
    case 'comment_pinned':
      return { lead: null, rest: 'Your comment was pinned', href }
    // Shipped by commentHub ahead of the contract listing it. Telling someone
    // their comment was removed is the point of the event — "my comment
    // vanished" is otherwise a silent reason people stop commenting — so it
    // gets real copy rather than the generic fallback.
    case 'comment_removed':
      return { lead: null, rest: 'Your comment was removed by a moderator', href }
    // The type that makes the bell fire for readers who are never replied to
    // directly. Commenting on an article auto-follows its conversation.
    case 'thread_reply': {
      // "N new" comes from unreadCount, never count: count is the group's total
      // size in the window, so an already-opened row would keep announcing new
      // comments after the badge went to zero — and would contradict its own
      // fallbackText, which drops the word "new" once read.
      const fresh = group.unreadCount ?? 0
      if (fresh > 1) {
        return { lead: null, rest: `${fresh} new comments on this conversation`, href }
      }
      if (fresh === 1) {
        return group.actors.length
          ? { lead: actorName(group), rest: 'commented on a conversation you joined', href }
          : { lead: null, rest: '1 new comment on this conversation', href }
      }
      const total = group.count
      return {
        lead: null,
        rest: total > 1 ? `${total} comments on this conversation` : 'A comment you followed',
        href,
      }
    }
    // The reader pressed Follow (commentHub writes it only on not following → following). The
    // row prints the post title beneath, so the sentence ends on "this conversation", like
    // thread_reply's. The link opens the article with the panel open: the conversation host
    // reads `#comments-section` on load.
    case 'thread_followed':
      return {
        lead: null,
        rest: "You'll be notified about new comments on this conversation",
        href: group.post?.url ? `${group.post.url}#comments-section` : null,
      }
    // The first type with no post and no comment (commentHub contract §10).
    // Roles are hub-wide — one account, one role, both brands — so the copy
    // names no site; and there is nowhere to deep-link, so href stays null and
    // the tray renders a static row.
    case 'role_changed': {
      const role = typeof group.meta?.role === 'string' ? group.meta.role : null
      const label = role ? roleLabels[role] : undefined
      let rest = group.fallbackText?.trim() || 'Your role changed'
      if (label) {
        rest = `You are now ${label}`
      } else if (role) {
        rest = `Your role changed to ${role.charAt(0).toUpperCase()}${role.slice(1)}`
      }
      return { lead: null, rest, href: null }
    }
    // Posted by another service (DealHunt's owner events on SoundGuys): no post and no actor,
    // so the server's sentence is the line and `meta.url` the link — the default branch would
    // read `post.url` and leave the row unclickable.
    case 'site_event':
      return {
        lead: null,
        rest: group.fallbackText?.trim() || 'You have a new notification',
        href: siteEventHref(group, context.appUrl),
      }
    // "Your device": a once-ever nudge to add a phone (no post, no actor). The link is `meta.url`
    // (`/profile/device/`) and only ever root-relative — an absolute URL, even on the own host,
    // does not link.
    case 'profile_prompt':
      return {
        lead: 'Which phone do you use?',
        rest: 'Add it to your profile and it shows beside your name on comments.',
        href: siteEventHref(group, null),
      }
    default:
      // The server always sends a rendered sentence for exactly this case, so
      // an unknown type reads correctly in a bundle that has never heard of it.
      // The literal is a floor for a server older than this contract, not a
      // fallback we expect to hit.
      return {
        lead: null,
        rest: group.fallbackText?.trim() || 'You have a new notification',
        href,
      }
  }
}

/** Faces are capped at three by the server; the overflow count comes from `count`. A system
 *  or service event has no actors at all — no faces, so nothing "+N" more of them. */
export function actorOverflow(group: CommentHubNotificationGroup): number {
  return group.actors.length ? Math.max(0, group.count - group.actors.length) : 0
}

const prefLabels: Record<string, string> = {
  reply: 'Replies to your comments',
  comment_liked: 'Likes on your comments',
  comment_featured: 'Your comment gets pinned',
  comment_pinned: 'Your comment gets pinned',
  comment_removed: 'Your comment gets removed',
  thread_reply: 'New comments on conversations you joined',
  role_changed: 'Your role changes (e.g. you become a Trusted User)',
  site_event: 'Updates from the site about your activity',
}

/**
 * The settings label for a notification type (the tray's inline settings, SoundGuys' member hub
 * grid). A type the server knows about but this bundle does not still needs a row —
 * `comment_removed` arrived exactly that way, ahead of the contract listing it. Sentence-case
 * the raw key so an unlabelled type reads as a setting rather than as a leaked identifier.
 */
export function notificationPrefLabel(type: string): string {
  const known = prefLabels[type]
  if (known) {
    return known
  }
  const words = type.replace(/_/g, ' ')
  return words.charAt(0).toUpperCase() + words.slice(1)
}
